import { ImageLoader } from './ImageLoader.worker';
import { EventChannel } from 'lbs-pi-handy/src/general-purpose/EventChannel';

const BATCH_SIZE = 10;
const QUEUE_INTERVAL = 50;

/*
TODO: use platform access instead of built-in urlbuilder
import { factory } from 'lbs-pi-platform-access/src/image-server/factory';
import axios from 'axios';
const { image: imageProvider } = factory(axios, options);
imageProvider.getTileUrl();
*/

export class RequestQueue {
  // Used to create urls for requests
  urlBuilder;
  // Keep track of all requests that have been made
  tracker = new Map();
  // Staging area for the next batch of images to request
  requestQueue = new Map();
  // Keeps track of all requests that are in progress
  processingQueue = new Map();
  // Used to signal when request queue is empty (mostly for performance metrics)
  events = new EventChannel();
  // Used to load images in a worker process
  imageLoader = new ImageLoader();

  constructor (urlBuilder) {
    this.urlBuilder = urlBuilder;

    // Set up a message router for messages from the image loader
    this.imageLoader.onmessage = (event) => {
      const { method, position, result, error } = event.data;
      const router = {
        loadImage: this.onRequestComplete,
        cancelImage: this.onCancelComplete
      };
      router[method].call(this, position, result, error);
    };
  }

  /**
   * Adds a request to the next batch.
   * @param request
   */
  enqueue (request) {
    if (this.tracker.has(request)) {
      return;
    }

    this.tracker.set(request.position, request);

    this.requestQueue.set(request.position, () => {
      this.processingQueue.set(request.position, request);
      setTimeout(() => this.processRequest(request));
    });
  }

  async processRequest (request) {
    let url = request.getUrl(this.urlBuilder);
    if (url.startsWith('/')) { // image loader needs full url or requests fail
      url = `${window.location.origin}${url}`;
    }
    this.imageLoader.postMessage({
      method: 'loadImage',
      position: request.position,
      url
    });
  }

  async onRequestComplete (position, result, error) {
    const request = this.processingQueue.get(position);

    if (error) {
      console.error('Error loading image at', position, error);
    } else {
      await request.onTextureLoaded(result);
      this.events.trigger('tile-loaded');
    }

    this.processingQueue.delete(position);
    this.maybeNotifyCompletion();
  }

  cancel (request) {
    // if not yet requested, just remove from request queue
    if (this.requestQueue.has(request.position)) {
      this.requestQueue.delete(request.position);
      this.tracker.delete(request.position);
    }

    // if request in progress, abort it
    if (this.processingQueue.has(request.position)) {
      this.imageLoader.postMessage({
        method: 'cancelImage',
        position: request.position
      });
    }
  }

  onCancelComplete (position, wasCancelled) {
    if (wasCancelled) {
      this.tracker.delete(position);
      this.processingQueue.delete(position);
    }
  }

  processQueue () {
    // Exit fast if nothing to process or already busy
    if (this.requestQueue.size === 0 || this.processingQueue.size > 0) {
      return;
    }

    let requestCount = 0;
    const batch = [];

    for (let position of this.requestQueue.keys()) {
      if (requestCount > BATCH_SIZE) {
        break;
      }
      requestCount++;
      batch.push(position);
    }
    batch.forEach(position => {
      const callback = this.requestQueue.get(position);
      if (callback) {
        setTimeout(callback);
        this.requestQueue.delete(position);
      }
    });
    if (this.requestQueue.size > 0) {
      this.monitorQueue();
    }
  }

  timeout;
  monitorQueue () {
    clearTimeout(this.timeout);

    try {
      this.processQueue();
    } catch (ex) {
      /* ignore errors */
    }

    this.timeout = setTimeout(() => this.monitorQueue(), QUEUE_INTERVAL);
  }

  maybeNotifyCompletion () {
    if (this.requestQueue.size === 0 && this.processingQueue.size === 0) {
      // for performance testing
      this.events.trigger('all-tiles-loaded');
    }
  }

  unload () {
    this.imageLoader.terminate();
  }
}
